/**
 * PKCE (RFC 7636) + OAuth `state` helpers for the CT partnership-linking
 * flow. The verifier and `state` are generated client-side, persisted in
 * `sessionStorage`, and consumed by the `/partner-linking/canadian-tire/
 * complete-linking` callback page when CT redirects back with
 * `?code=&state=`.
 *
 * We persist `{codeVerifier, source}` keyed by `state` so:
 *   1. the callback can verify the returned `state` matches one we minted
 *      (CSRF defense per RFC 6749 §10.12),
 *   2. the callback can recover the matching `codeVerifier` (PKCE) and the
 *      `source` (THIWEB / THIAPP) the user kicked off with.
 *
 * `sessionStorage` (not `localStorage`) intentionally — the linking flow is
 * single-tab, single-session; the verifier should die with the tab.
 */

const STORAGE_PREFIX = 'ct-partner-linking:';

const PKCE_VERIFIER_LENGTH = 64;
const STATE_LENGTH = 24;

export interface IStoredPkce {
  codeVerifier: string;
  source: string;
}

export interface IPkceChallenge {
  codeVerifier: string;
  codeChallenge: string;
  state: string;
}

export async function generatePkceChallenge(): Promise<IPkceChallenge> {
  const codeVerifier = base64UrlEncode(randomBytes(PKCE_VERIFIER_LENGTH));
  const state = base64UrlEncode(randomBytes(STATE_LENGTH));
  const challengeBytes = await sha256(codeVerifier);
  return {
    codeVerifier,
    codeChallenge: base64UrlEncode(challengeBytes),
    state,
  };
}

/** Persists the verifier + originating `source` keyed by `state`. */
export function storePkce(state: string, value: IStoredPkce): void {
  window.sessionStorage.setItem(STORAGE_PREFIX + state, JSON.stringify(value));
}

/**
 * Pops the stored verifier+source for a given `state` (single-use — the
 * entry is removed on read so a replay of the same callback URL fails).
 * Returns `null` if missing, malformed, or sessionStorage is unavailable.
 */
export function consumePkce(state: string): IStoredPkce | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + state);
    if (!raw) {
      return null;
    }
    window.sessionStorage.removeItem(STORAGE_PREFIX + state);
    const parsed = JSON.parse(raw) as IStoredPkce;
    if (typeof parsed?.codeVerifier !== 'string' || typeof parsed?.source !== 'string') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return bytes;
}

async function sha256(input: string): Promise<Uint8Array> {
  const bytes = new TextEncoder().encode(input);
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return new Uint8Array(digest);
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
